import type { AnalysisToken, AnalysisArc } from '@/types/sentence'
import { posStyle } from './posStyles'

interface WordChipsProps {
  tokens: AnalysisToken[]
  arcs: AnalysisArc[]
  activeIndex: number | null
  onHover: (index: number | null) => void
  onClick: (index: number) => void
}

export function WordChips({ tokens, arcs, activeIndex, onHover, onClick }: WordChipsProps) {
  const related = new Set<number>()
  if (activeIndex !== null) {
    related.add(activeIndex)
    for (const arc of arcs) {
      if (arc.from === activeIndex || arc.to === activeIndex) {
        related.add(arc.from)
        related.add(arc.to)
      }
    }
  }

  return (
    <div className="flex flex-wrap justify-center gap-3" role="list" aria-label="Sentence words">
      {tokens.map((token) => {
        const s = posStyle(token.pos)
        const isActive = activeIndex === token.index
        const isFaded = activeIndex !== null && !related.has(token.index)

        return (
          <button
            key={token.index}
            role="listitem"
            className="flex flex-col items-center gap-1 min-h-11"
            onMouseEnter={() => onHover(token.index)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onClick(token.index)}
            aria-label={`${token.word}, ${s.label}`}
            aria-pressed={isActive}
          >
            <span
              data-chip-index={token.index}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-all duration-150 ${s.bg} ${s.border} ${s.text} ${
                isActive ? 'ring-2 ring-border-divider' : ''
              } ${isFaded ? 'opacity-20' : ''}`}
            >
              {token.word}
            </span>
            <span
              className={`text-[10px] font-light uppercase tracking-wider text-muted transition-opacity duration-150 ${
                isFaded ? 'opacity-20' : ''
              }`}
            >
              {s.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
